// @flow
import React from 'react';
import {FormattedMessage} from 'react-intl';
import Icon from '../Icon/Icon';
import Price from '../Price/Price';
import Rating from '../Rating/Rating';
import TruncatedText from '../TruncatedText/TruncatedText';
import HitSuperlative from '../HitSuperlative/HitSuperlative';
import Details from '../Details/Details';
import type {Props as HitInfoProps} from './HitInfo';
import HitFreeCancellation from '../HitFreeCancellation/HitFreeCancellation';

type Props = {
    showPartnerBadge: boolean,
    instantBookableTitle: string,
    premierPartnerDescription: string,
    showMobileHitHeadlines: boolean
} & HitInfoProps;

export default function HitInfoMobile({
    amenitiesBadges,
    averageRating,
    details,
    freeCancellation,
    freeCancellationVariant,
    instantBookable,
    showMobileHitHeadlines,
    headline,
    minStay,
    price,
    priceSummary,
    propertyType,
    reviewCount,
    showPartnerBadge,
    superlative,
    instantBookableTitle,
    isDatelessSearch,
    isMetric,
    locale
}: Props) {
    return (
        <div className="HitInfo HitInfo--mobile">
            <div className="HitInfo__content">
                <div className="HitInfo__priceSuperlativeGroup">
                    <div className="HitInfo__price">
                        {instantBookable && (
                            <Icon
                                name="instant-book"
                                iconClass="InstantBook__icon"
                                title={instantBookableTitle}
                            />
                        )}
                        <Price
                            price={price}
                            priceSummary={priceSummary}
                            useAverage={isDatelessSearch}
                            locale={locale}
                        />
                    </div>
                    {/* SERP 979 CONTROL - Just show superlative  */}
                    {freeCancellationVariant === 0 &&
                        !!superlative && (
                            <span className="HitInfo__superlative small">
                                <HitSuperlative {...superlative} />
                            </span>
                        )}
                    {/* SERP 979 VARIANT 1 - Only show free cancellation, or VARIANT 3 - Show free cancellation here, and superlatives next to ratings */}
                    {(freeCancellationVariant === 1 ||
                        freeCancellationVariant === 3) &&
                        !!freeCancellation && (
                            <span className="HitInfo__cancellation small">
                                <HitFreeCancellation />
                            </span>
                        )}
                    {/* SERP 979 VARIANT 2 - Show free cancellation if it exists - if not, show superlative */}
                    {freeCancellationVariant === 2 &&
                        ((!!freeCancellation && (
                            <span className="HitInfo__cancellation small">
                                <HitFreeCancellation />
                            </span>
                        )) ||
                            (!!superlative && (
                                <span className="HitInfo__superlative small">
                                    <HitSuperlative {...superlative} />
                                </span>
                            )))}
                </div>
                {showMobileHitHeadlines && (
                    <TruncatedText
                        lines={2}
                        className="HitInfo__textBox"
                        id="HitInfo__textBox"
                    >
                        <div className="HitInfo__headline ">{headline}</div>
                    </TruncatedText>
                )}

                <div className="HitInfo__details">
                    <Details
                        amenitiesBadges={amenitiesBadges}
                        details={details}
                        isMetric={isMetric}
                        propertyType={propertyType}
                    />
                </div>
                {minStay &&
                    isDatelessSearch && (
                        <div className="HitInfo__minStay">
                            <FormattedMessage
                                id="minStayLabel"
                                description="min stay"
                                defaultMessage="{MIN_STAY} night min stay"
                                values={{
                                    MIN_STAY: minStay
                                }}
                            />
                        </div>
                    )}
                <div className="HitInfo__badgeRatingGroup">
                    {reviewCount > 0 && (
                        <div className="HitInfo__rating">
                            <Rating
                                averageRating={averageRating}
                                reviewCount={reviewCount}
                            />
                        </div>
                    )}

                    {/* SERP 979 VARIANT 3 - Show the superlative next to the rating  */}
                    {freeCancellationVariant === 3 && (
                        <div className="HitInfo__lowerSuperlative">
                            {!!superlative && (
                                <span className="HitInfo__superlative small">
                                    <HitSuperlative {...superlative} />
                                </span>
                            )}
                        </div>
                    )}

                    {/* SERP 979 VARIANTS 0,1,2 - For every variant except 3, keep the partner badge on the same line */}
                    {freeCancellationVariant !== 3 &&
                        showPartnerBadge && (
                            <div
                                className="HitInfo__premierBadge bg-brand small"
                                data-wdio="premier-partner-badge"
                            >
                                <FormattedMessage
                                    defaultMessage="Premier Partner"
                                    description="Premier Partner"
                                    id="premierPartnerLabel"
                                />
                            </div>
                        )}
                </div>
                {/* SERP 979 VARIANT 3 - Need to put partner badge onto next line */}
                {freeCancellationVariant === 3 && (
                    <div className="HitInfo_premierBadge">
                        {showPartnerBadge && (
                            <div
                                className="HitInfo__premierBadge bg-brand small"
                                data-wdio="premier-partner-badge"
                            >
                                <FormattedMessage
                                    defaultMessage="Premier Partner"
                                    description="Premier Partner"
                                    id="premierPartnerLabel"
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
